import React, { useState, useEffect } from 'react';
import { useAppContext } from '../../contexts/AppContext';
import { NAV_LINKS } from '../../constants';
import { User, NavLinkItem } from '../../types';
import { Users, Shield, CheckSquare, Square, Plus, Key, Save, Sparkles } from 'lucide-react';
import { saveUserToStorage } from '../../utils/userStorage';
import { EcnPermissionToggle } from '../../components/admin/EcnPermissionToggle';

const PermissionsDashboard: React.FC = () => {
    const { users, updateUserPermissions, addUser, changePassword } = useAppContext();
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [userPermissions, setUserPermissions] = useState<Set<string>>(new Set());
    const [showAddUserModal, setShowAddUserModal] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);

    // Add User Form State
    const [newUserUsername, setNewUserUsername] = useState('');
    const [newUserPassword, setNewUserPassword] = useState('');
    const [newUserRole, setNewUserRole] = useState<'admin' | 'user' | 'magnus'>('user');

    // Change Password Form State
    const [newPassword, setNewPassword] = useState('');

    // Flatten NAV_LINKS to get all available app paths
    const allApps = NAV_LINKS.reduce((acc, link) => {
        if (link.children) {
            acc.push(...link.children);
        } else {
            acc.push(link);
        }
        return acc;
    }, [] as NavLinkItem[]);

    useEffect(() => {
        if (selectedUser) {
            // Update the selected user from the context users list to reflect any changes
            const currentUser = users.find(u => u.username === selectedUser.username) || null;
            if (currentUser) {
                setUserPermissions(new Set(currentUser.permissions));
            } else {
                setSelectedUser(null);
            }
        }
    }, [users]); // Re-run when users list changes

    const handleUserSelect = (username: string) => {
        const user = users.find(u => u.username === username);
        if (user) {
            setSelectedUser(user);
            setUserPermissions(new Set(user.permissions));
        } else {
            setSelectedUser(null);
        }
    };

    const handlePermissionToggle = (path: string) => {
        const newPermissions = new Set(userPermissions);
        if (newPermissions.has(path)) {
            newPermissions.delete(path);
        } else {
            newPermissions.add(path);
        }
        setUserPermissions(newPermissions);
    };


    const handleSaveChanges = async () => {
        if (selectedUser) {
            const permissionsArray: string[] = Array.from(userPermissions);

            // Update selectedUser object with latest permissions
            const updatedUser = { ...selectedUser, permissions: permissionsArray };

            // Update basic permissions first
            await updateUserPermissions(selectedUser.username, permissionsArray);

            // Save full user object with ECN properties and updated permissions to database
            // Save full user object with ECN properties and updated permissions to database
            await saveUserToStorage(updatedUser);

            alert('Permissions and ECN roles updated successfully!');
        }
    };

    const handleAddUser = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newUserUsername || !newUserPassword) {
            alert("Username and password are required.");
            return;
        }
        if (users.some(u => u.username === newUserUsername)) {
            alert("Username already exists.");
            return;
        }

        const newUser: User = {
            uid: `user_${Date.now()}`,
            username: newUserUsername,
            password: newUserPassword,
            role: newUserRole,
            permissions: [] // Start with no permissions
        };

        await addUser(newUser);
        setNewUserUsername('');
        setNewUserPassword('');
        setNewUserRole('user');
        setShowAddUserModal(false);
        alert(`User ${newUser.username} added successfully.`);
    };

    const handleChangePassword = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedUser) return;
        if (!newPassword) {
            alert("New password cannot be empty.");
            return;
        }

        await changePassword(selectedUser.username, newPassword);
        setNewPassword('');
        setShowPasswordModal(false);
        alert(`Password for ${selectedUser.username} updated successfully.`);
    };

    return (
        <div className="space-y-6 relative">
            {/* Header */}
            <div className="flex justify-between items-start">
                <div>
                    <h1 className="text-3xl font-bold text-text-primary flex items-center gap-3">
                        <Shield className="text-primary" /> Administrator Dashboard
                    </h1>
                    <p className="text-text-secondary mt-1">Manage user access to different application modules.</p>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-card border border-border rounded-lg p-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                            <Users className="text-blue-600" size={20} />
                        </div>
                        <div>
                            <div className="text-2xl font-bold">{users.length}</div>
                            <div className="text-sm text-text-secondary">Total Users</div>
                        </div>
                    </div>
                </div>
                <div className="bg-card border border-border rounded-lg p-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                            <Shield className="text-purple-600" size={20} />
                        </div>
                        <div>
                            <div className="text-2xl font-bold">{users.filter(u => u.role === 'admin' || u.role === 'autoGainAdmin' || u.role === 'clientAdmin').length}</div>
                            <div className="text-sm text-text-secondary">Administrators</div>
                        </div>
                    </div>
                </div>
                <div className="bg-card border border-border rounded-lg p-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                            <CheckSquare className="text-green-600" size={20} />
                        </div>
                        <div>
                            <div className="text-2xl font-bold">{allApps.length}</div>
                            <div className="text-sm text-text-secondary">App Modules</div>
                        </div>
                    </div>
                </div>
                <div className="bg-card border border-border rounded-lg p-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                            <Sparkles className="text-amber-600" size={20} />
                        </div>
                        <div>
                            <div className="text-2xl font-bold">{users.filter(u => u.permissions.includes('magnus-assistant') || u.permissions[0] === '*').length}</div>
                            <div className="text-sm text-text-secondary">AI Enabled</div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
                {/* User List */}
                <div className="lg:col-span-1 bg-card border border-border rounded-lg shadow-sm">
                    <div className="p-4 border-b border-border flex justify-between items-center">
                        <h2 className="font-bold text-lg flex items-center gap-2">
                            <Users size={20} /> Users
                        </h2>
                        <button
                            onClick={() => setShowAddUserModal(true)}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary text-white text-sm hover:bg-primary/90 transition-colors"
                            title="Add User"
                        >
                            <Plus size={16} />
                            Add
                        </button>
                    </div>
                    <ul className="divide-y divide-border max-h-[500px] overflow-y-auto">
                        {users.map(user => (
                            <li
                                key={user.username}
                                onClick={() => handleUserSelect(user.username)}
                                className={`p-4 cursor-pointer hover:bg-background/80 transition-colors ${selectedUser?.username === user.username ? 'bg-primary/10 border-l-4 border-l-primary' : ''}`}
                            >
                                <div className="flex justify-between items-center">
                                    <div>
                                        <div className="font-medium text-text-primary">{user.username}</div>
                                        <div className="text-xs text-text-secondary uppercase">{user.role}</div>
                                    </div>
                                    {(user.role === 'admin' || user.role === 'autoGainAdmin' || user.role === 'clientAdmin') && (
                                        <span className="px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400">Admin</span>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Permissions Panel */}
                <div className="lg:col-span-2 bg-card border border-border rounded-lg shadow-sm">
                    <div className="p-4 border-b border-border flex justify-between items-center">
                        <h2 className="font-bold text-lg">Application Access</h2>
                        {selectedUser && (
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setShowPasswordModal(true)}
                                    className="px-3 py-2 bg-gray-200 dark:bg-gray-700 text-text-primary text-sm font-medium rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 flex items-center gap-2"
                                >
                                    <Key size={16} /> Change Password
                                </button>
                                <button
                                    onClick={handleSaveChanges}
                                    className="px-4 py-2 bg-primary text-white text-sm font-medium rounded-md hover:bg-primary/90 flex items-center gap-2"
                                >
                                    <Save size={16} /> Save Changes
                                </button>
                            </div>
                        )}
                    </div>

                    {selectedUser ? (
                        <div className="p-4">
                            <p className="mb-4">
                                <span className="font-medium">{selectedUser.username}</span> has access to the following modules.
                            </p>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {allApps.map(app => (
                                    <div
                                        key={app.path}
                                        onClick={() => handlePermissionToggle(app.path)}
                                        className={`flex items-center gap-3 p-3 rounded-md cursor-pointer border transition-colors ${userPermissions.has(app.path) || selectedUser.permissions[0] === '*'
                                            ? 'bg-primary/5 border-primary/20'
                                            : 'border-border hover:bg-background/80'
                                            }`}
                                    >
                                        {userPermissions.has(app.path) || selectedUser.permissions[0] === '*' ? (
                                            <CheckSquare className="text-primary" size={20} />
                                        ) : (
                                            <Square className="text-text-secondary/50" size={20} />
                                        )}
                                        <div className="flex items-center gap-2">
                                            {app.icon}
                                            <span className="text-sm font-medium">{app.name}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Magnus Assistant Feature Toggle */}
                            <div className="mt-6 pt-6 border-t border-border">
                                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                                    <Sparkles size={18} className="text-purple-500" />
                                    AI Features
                                </h3>
                                <p className="text-sm text-text-secondary mb-4">
                                    Enable or disable AI-powered assistants and tools
                                </p>

                                <div
                                    onClick={() => handlePermissionToggle('magnus-assistant')}
                                    className={`flex items-start gap-3 p-4 rounded-lg cursor-pointer border transition-colors ${userPermissions.has('magnus-assistant') || selectedUser.permissions[0] === '*'
                                        ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800'
                                        : 'bg-background hover:bg-gray-50 dark:hover:bg-gray-800 border-border'
                                        }`}
                                >
                                    {userPermissions.has('magnus-assistant') || selectedUser.permissions[0] === '*' ? (
                                        <CheckSquare className="text-purple-600 dark:text-purple-400 flex-shrink-0" size={20} />
                                    ) : (
                                        <Square className="text-text-secondary/50 flex-shrink-0" size={20} />
                                    )}
                                    <div className="flex-1">
                                        <div className="font-medium text-text-primary flex items-center gap-2">
                                            Magnus Assistant
                                            <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300">
                                                AI
                                            </span>
                                        </div>
                                        <p className="text-sm text-text-secondary mt-1">
                                            Context-aware navigation assistant with FMEA expertise. Appears as a floating button in bottom-right corner when enabled.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* ECN-Specific Permissions */}
                            {userPermissions.has('/ecn-management') && (
                                <div className="mt-6 pt-6 border-t border-border">
                                    <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                                        <Shield size={18} className="text-primary" />
                                        ECN Management Roles
                                    </h3>
                                    <p className="text-sm text-text-secondary mb-4">
                                        Configure specific permissions for Engineering Change Notice workflow
                                    </p>

                                    <div className="space-y-4">
                                        {/* User Initials */}
                                        <div className="bg-background p-4 rounded-lg border border-border">
                                            <label className="block text-sm font-medium mb-2">
                                                User Initials (for ECN tracking)
                                            </label>
                                            <input
                                                type="text"
                                                value={selectedUser.initials || ''}
                                                onChange={(e) => {
                                                    const updatedUser = { ...selectedUser, initials: e.target.value.toUpperCase() };
                                                    setSelectedUser(updatedUser);
                                                }}
                                                maxLength={4}
                                                placeholder="e.g., WK, JS, MC"
                                                className="w-32 px-3 py-2 bg-card border border-border rounded focus:ring-2 focus:ring-primary outline-none uppercase"
                                            />
                                            <p className="text-xs text-text-secondary mt-1">
                                                2-4 characters (will appear on ECN submissions and approvals)
                                            </p>
                                        </div>

                                        {/* Create/Edit Permission */}
                                        <EcnPermissionToggle
                                            checked={selectedUser.ecnCanCreate || false}
                                            onChange={() => {
                                                const updated = { ...selectedUser, ecnCanCreate: !selectedUser.ecnCanCreate };
                                                setSelectedUser(updated);
                                            }}
                                            title="Can Create & Edit ECNs"
                                            description="User can create new ECNs, edit drafts, edit rejected ECNs, submit for review, and recall submitted ECNs"
                                            colorClass="green"
                                        />

                                        {/* Approve Permission */}
                                        <EcnPermissionToggle
                                            checked={selectedUser.ecnCanApprove || false}
                                            onChange={() => {
                                                const updated = { ...selectedUser, ecnCanApprove: !selectedUser.ecnCanApprove };
                                                setSelectedUser(updated);
                                            }}
                                            title="Can Review & Approve ECNs"
                                            description="User can review submitted ECNs and approve or reject them with comments"
                                            colorClass="blue"
                                        />

                                        {/* Role Summary */}
                                        <div className="bg-primary/5 border border-primary/20 p-4 rounded-lg">
                                            <div className="text-sm font-medium text-primary mb-2">ECN Role Summary:</div>
                                            <ul className="text-sm text-text-secondary space-y-1">
                                                {selectedUser.ecnCanCreate && (
                                                    <li>✓ Can create, edit, submit, and recall ECNs</li>
                                                )}
                                                {selectedUser.ecnCanApprove && (
                                                    <li>✓ Can review and approve/reject ECNs</li>
                                                )}
                                                {!selectedUser.ecnCanCreate && !selectedUser.ecnCanApprove && (
                                                    <li className="text-text-secondary/50">✗ Read-only access to ECN Management</li>
                                                )}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="p-12 text-center text-text-secondary">
                            <p>Select a user to manage their permissions.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Add User Modal */}
            {showAddUserModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-card p-6 rounded-lg shadow-lg w-full max-w-md border border-border">
                        <h3 className="text-xl font-bold mb-4">Add New User</h3>
                        <form onSubmit={handleAddUser} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Username</label>
                                <input
                                    type="text"
                                    value={newUserUsername}
                                    onChange={(e) => setNewUserUsername(e.target.value)}
                                    className="w-full p-2 rounded border border-border bg-background"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Password</label>
                                <input
                                    type="password"
                                    value={newUserPassword}
                                    onChange={(e) => setNewUserPassword(e.target.value)}
                                    className="w-full p-2 rounded border border-border bg-background"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Role</label>
                                <select
                                    value={newUserRole}
                                    onChange={(e) => setNewUserRole(e.target.value as 'admin' | 'user' | 'magnus')}
                                    className="w-full p-2 rounded border border-border bg-background"
                                >
                                    <option value="user">User</option>
                                    <option value="admin">Admin</option>
                                    <option value="magnus">Magnus (Admin + App Access)</option>
                                </select>
                            </div>
                            <div className="flex justify-end gap-3 mt-6">
                                <button
                                    type="button"
                                    onClick={() => setShowAddUserModal(false)}
                                    className="px-4 py-2 text-sm text-text-secondary hover:bg-background/50 rounded"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-primary text-white rounded text-sm font-medium hover:bg-primary/90"
                                >
                                    Add User
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Change Password Modal */}
            {showPasswordModal && selectedUser && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-card p-6 rounded-lg shadow-lg w-full max-w-md border border-border">
                        <h3 className="text-xl font-bold mb-4">Change Password for {selectedUser.username}</h3>
                        <form onSubmit={handleChangePassword} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">New Password</label>
                                <input
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    className="w-full p-2 rounded border border-border bg-background"
                                    required
                                />
                            </div>
                            <div className="flex justify-end gap-3 mt-6">
                                <button
                                    type="button"
                                    onClick={() => setShowPasswordModal(false)}
                                    className="px-4 py-2 text-sm text-text-secondary hover:bg-background/50 rounded"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-primary text-white rounded text-sm font-medium hover:bg-primary/90"
                                >
                                    Update Password
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PermissionsDashboard;
